import { useEffect } from "react"
import { useQuery } from "react-query"
import Select from "react-select"
import axios from "axios"
import cn from "classnames"
import customStyles from "./styles"
import DropdownIndicatorForSelect from "components/DropdownIndicatorForSelect"

const fetchCustomerLocations = (id?: number) => async () => {
  if (!id) return
  const { data } = await axios.get(`/customers/${id}/locations?ordering=name`)

  const output = data.map((location: any) => ({
    value: location.id,
    label: location?.location_name ?? location.address,
    ...location,
  }))

  return output
}

const CustomerLocationPicker = (props: {
  isLoading?: boolean
  selected?: number
  locations?: {
    id: number
    location_name: string
    value?: number
    label: string
  }[]
  label?: string
  isClearable?: boolean
  displayOnly?: boolean
  flush?: boolean
  className?: string
  customerId?: number
  preselect?: number
  errorText?: string | boolean
  skipSingleAutoSelect?: boolean
  onSelect?: (item: any) => void
}) => {
  const errorColor = props.errorText ? "#cc0000" : "#cbdfea"
  const { data: locations, isLoading } = useQuery(
    ["customer", props.customerId, "locations"],
    fetchCustomerLocations(props.customerId),
    { enabled: !props?.locations?.length }
  )

  const data = props.locations ?? locations ?? []

  useEffect(() => {
    if (!data) return
    if (data.length === 1 && props.onSelect && !props.skipSingleAutoSelect)
      props.onSelect({ ...data[0] })
    if (props.preselect) {
      const item = data.find((i: any) => i.id === props.preselect)
      if (item) props?.onSelect?.({ ...item })
    }
  }, [data])

  if (!props.customerId) return <div />

  if (isLoading || props.isLoading)
    return (
      <div className={cn("flex-c-box mr-6", props.className)}>
        {!props.label ? null : (
          <>
            <span style={{ color: "#486f88", fontWeight: "bold" }}>
              {props.label}
            </span>
            <div style={{ marginTop: 8 }} />
          </>
        )}
        <span style={{ color: "#223345" }}>Loading</span>
      </div>
    )

  const styles = {
    maxWidth: 240,
    border: `1px solid ${errorColor}`,
  } as any
  if (props.flush) styles.marginRight = 0

  return (
    <div className={cn("flex-c-box", props.className)}>
      {!props.label ? null : (
        <>
          <span style={{ color: "#486f88", fontWeight: "bold" }}>
            {props.label}
          </span>
          <div style={{ marginTop: 6 }} />
        </>
      )}

      {data.length === 1 ? (
        <div
          className="h-10 flex rounded bg-primary-bg mr-6 pl-2 pr-4 py-1 items-center justify-center"
          style={{ border: `1px solid ${errorColor}` }}
        >
          <span style={{ color: "#223345", fontSize: 13 }}>
            {data[0].location_name}
          </span>
        </div>
      ) : (
        <Select
          isDisabled={props.displayOnly}
          components={
            props.errorText
              ? { DropdownIndicator: DropdownIndicatorForSelect }
              : {}
          }
          isClearable={props.isClearable}
          placeholder="Location Name"
          options={data}
          getOptionLabel={(option) => option?.label ?? option?.location_name}
          getOptionValue={(option) => option?.value ?? option.id}
          onChange={props.onSelect}
          styles={customStyles(styles)}
          value={
            props?.selected
              ? data?.find(
                  (item: { value: number }) => item?.value === props?.selected
                )
              : null
          }
        />
      )}
    </div>
  )
}

export default CustomerLocationPicker
